import React, { useRef, useState } from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Input, 
  Textarea, 
  Select, 
  Button, 
  FormControl, 
  FormLabel,
  useToast,
  VStack,
  useColorMode
} from '@chakra-ui/react';
import { API_URL } from '../api';
import './Contact.css';

const EMPTY_FORM = {
  names: '',
  email: '',
  photoType: '',
  date: '',
  message: '',
  howDidYouFind: '',
  // Past na roboty – člověk pole nevidí, takže zůstane prázdné
  website: '',
};

const Contact: React.FC = () => {
  const { colorMode } = useColorMode();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Od kdy je formulář na stránce – backend zprávu vyplněnou za pár vteřin
  // bere jako robota (člověk musí dojet ke kontaktu a napsat jméno i zprávu)
  const shownAtRef = useRef(Date.now());
  const toast = useToast();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Send only what the backend expects – no extra fields
        body: JSON.stringify({
          names: formData.names,
          email: formData.email,
          photoType: formData.photoType,
          date: formData.date,
          message: formData.message,
          howDidYouFind: formData.howDidYouFind,
          website: formData.website,
          fillMs: Date.now() - shownAtRef.current,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        // Show server-side validation errors if available
        const detail =
          Array.isArray(body?.fields) && body.fields.length > 0
            ? body.fields.map((f: { message: string }) => f.message).join(' ')
            : 'Zkuste to prosím znovu.';
        throw new Error(detail);
      }

      toast({
        title: 'Zpráva odeslána!',
        description: 'Děkuji za zprávu. Ozvu se do 48 hodin.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setFormData(EMPTY_FORM);
      shownAtRef.current = Date.now();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Něco se pokazilo.';
      toast({
        title: 'Chyba při odesílání',
        description: message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box 
      id="contact" 
      className="contact-container"
      bg={colorMode === 'dark' ? 'gray.800' : '#f8f8f8'}
    >
      <Flex className="contact-content">
        <Box
          className="contact-image-section"
          style={{
            // Kterou variantu vzít, rozhoduje režim webu (Contact.css + darkMode.css)
            ['--contact-photo' as string]: `url(${process.env.PUBLIC_URL}/photos/svatebni2.webp)`,
            ['--contact-photo-bw' as string]: `url(${process.env.PUBLIC_URL}/photos/svatebni2-bw.webp)`,
          }}
        />

        <Box 
          className="contact-form-section reveal" 
          bg={colorMode === 'dark' ? 'gray.800' : '#f8f8f8'}
        >
          <Text 
            className="contact-title" 
            color={colorMode === 'dark' ? 'white' : 'black'}
          >
            Kontakt
          </Text>
          <Text 
            className="contact-subtitle"
            color={colorMode === 'dark' ? 'gray.300' : 'gray.600'}
          >
            Pokud se vám líbí moje práce, budu ráda, když se ozvete!
          </Text>
          
          <Box as="form" onSubmit={handleSubmit} className="contact-form">
            {/* Past na roboty – mimo obrazovku, čtečky i klávesnice ji přeskočí */}
            <Box className="contact-hp" aria-hidden="true">
              <label>
                Web
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleInputChange}
                />
              </label>
            </Box>

            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel 
                  className="form-label"
                  color={colorMode === 'dark' ? 'gray.300' : 'black'}
                >
                  Vaše jména
                </FormLabel>
                <Input
                  name="names"
                  value={formData.names}
                  onChange={handleInputChange}
                  className="form-input"
                  bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                  color={colorMode === 'dark' ? 'white' : 'black'}
                  borderColor={colorMode === 'dark' ? 'gray.600' : '#ddd'}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel 
                  className="form-label"
                  color={colorMode === 'dark' ? 'gray.300' : 'black'}
                >
                  Email
                </FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                  color={colorMode === 'dark' ? 'white' : 'black'}
                  borderColor={colorMode === 'dark' ? 'gray.600' : '#ddd'}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel 
                  className="form-label"
                  color={colorMode === 'dark' ? 'gray.300' : 'black'}
                >
                  Typ fotografování
                </FormLabel>
                <Select
                  name="photoType"
                  value={formData.photoType}
                  onChange={handleInputChange}
                  className="form-select"
                  placeholder=" "
                  bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                  color={colorMode === 'dark' ? 'white' : 'black'}
                  borderColor={colorMode === 'dark' ? 'gray.600' : '#ddd'}
                >
                  <option value="rodinne">Rodinné</option>
                  <option value="parove">Párové</option>
                  <option value="portretni">Portrétní</option>
                  <option value="svatebni">Svatební</option>
                  <option value="produktove">Produktové / brandové</option>
                  <option value="jine">Jiné</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel
                  className="form-label"
                  color={colorMode === 'dark' ? 'gray.300' : 'black'}
                >
                  Váš termín?
                </FormLabel>
                <Select
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="form-select"
                  placeholder=" "
                  bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                  color={colorMode === 'dark' ? 'white' : 'black'}
                  borderColor={colorMode === 'dark' ? 'gray.600' : '#ddd'}
                >
                  <option value="flexibilni">Flexibilní</option>
                  <option value="tyden">Týden</option>
                  <option value="vikend">Víkend</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel 
                  className="form-label"
                  color={colorMode === 'dark' ? 'gray.300' : 'black'}
                >
                  Zpráva
                </FormLabel>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows={4}
                  bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                  color={colorMode === 'dark' ? 'white' : 'black'}
                  borderColor={colorMode === 'dark' ? 'gray.600' : '#ddd'}
                />
              </FormControl>

              <FormControl>
                <FormLabel 
                  className="form-label"
                  color={colorMode === 'dark' ? 'gray.300' : 'black'}
                >
                  Jak jste mě našli?
                </FormLabel>
                <Select
                  name="howDidYouFind"
                  value={formData.howDidYouFind}
                  onChange={handleInputChange}
                  className="form-select"
                  placeholder=" "
                  bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                  color={colorMode === 'dark' ? 'white' : 'black'}
                  borderColor={colorMode === 'dark' ? 'gray.600' : '#ddd'}
                >
                  <option value="google">Google</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="doporuceni">Doporučení přátel</option>
                  <option value="jine">Jiné</option>
                </Select>
              </FormControl>

              <Button
                type="submit"
                isLoading={isSubmitting}
                loadingText="Odesílám..."
                className="submit-button"
                size="lg"
              >
                Odeslat
              </Button>
            </VStack>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default Contact;
